import styled, { css, Interpolation } from 'styled-components';
import type { HTMLAttributes } from 'react';
import _ from 'lodash';

type PropMapper = (value: unknown, prop: string, props: Record<string, unknown>) => Interpolation<any>;

export const mapProps = (map: Record<string, unknown>): Interpolation<any> => {
    const onProp = (prop: string, result: unknown) =>
        (props: Record<string, unknown>): Interpolation<any> => {
            const value = _.get(props, prop);
            if (value == null) return undefined;
            if (value === false) return null;
            if (_.isFunction(result)) return (result as PropMapper)(value, prop, props);
            return result !== true ? (result as Interpolation<any>) : (value as Interpolation<any>);
        };

    const mapped = _.map(map, (fn, prop) => onProp(prop, fn));

    return (props: Record<string, unknown>) => mapped.map(fn => fn(props));
};


/** Custom props these style helpers accept (consumed by `mapProps`). Declared explicitly
 *  (not an index signature) because styled-components v5 strips all intrinsic props when
 *  the props generic has a string index signature. */
interface StyleProps extends HTMLAttributes<HTMLElement> {
    grow?: boolean;
    auto?: boolean;
    flex?: boolean;
    block?: boolean;
    hover?: boolean;
    primary?: boolean;
    green?: boolean;
    delete?: boolean;
    centered?: boolean;
    disabled?: boolean;
    flexStart?: boolean;
    table?: boolean;
    margin?: number | string;
    'margin-top'?: number | string;
    size?: number | string;
}

export const FlexItem = styled.div<StyleProps>`
    ${mapProps({
        grow: css`flex: 1 1 0;`,
        auto: css`flex: 0 0 auto!important;`,
        flex: css`display: flex;`,
    })}
`;

export const AddNewItem = styled.div<StyleProps>`
    display: inline-block;
    margin-top: .5rem;
    color: royalblue;
    cursor: pointer;
`;

/** @deprecated */
export const Button = styled(FlexItem)<StyleProps>`
    display: inline-block;
    border: 0;
    border-radius: .5rem;
    box-shadow: 0 0 1px #aaa inset;
    background: #fafafa;
    padding: .3rem 1rem;
    font-size: .8rem;
    cursor: pointer;
    overflow: hidden;
    user-select: none;
    transition: background .2s ease, color .2s ease, transform .1s ease;
    outline: none;

    &:active { transform: translateX(2px) translateY(2px); }

    ${mapProps({
        block: css`display: block;`,
        hover: css`
            &:hover, &:focus {
                background: darkturquoise;
                color: white;
            }
        `,
        primary: css`
            background: royalblue;
            color: white;
        `,
        green: css`
            background: lightgreen;
        `,
        delete: css`
            background: crimson;
            color: white;

            &:hover, &:focus {
                background: darkred;
                color: white;
            }
        `,
        centered: css`
            display: block;
            margin: 0 auto;
            padding: .5rem;
            width: 10rem;
            font-size: 1rem;
            text-align: center;
        `,
        disabled: css`
            background: #ccc!important;
            cursor: not-allowed;
        `,
        margin: (val: number | string) => css`margin-top: ${val}rem; margin-bottom: ${val}rem`,
    })}
`;

export const LabelName = styled.div<StyleProps>`
    flex: 0 0 auto;
    margin-right: .5rem;
    min-width: 5rem;
    font-weight: bold;
    ${mapProps({
        size: (size: number | string) => css`min-width: ${size}rem;`,
    })}
`;

export const Label = styled.label<StyleProps>`
    display: flex;
    align-items: center;

    & + & {
        margin-top: .5rem;
    }

    > :not(${LabelName}) {
        flex: 1 1 0;
    }
`;

export const Row = styled.div<StyleProps>`
    display: flex;
    align-items: center;

    ${mapProps({
        margin: (val: number | string) => css`margin: ${val}rem 0;`,

        'margin-top': (val: number | string) => css`margin-top: ${val}rem;`,

        flexStart: css`align-items: flex-start`,
    })}
`;

export const Err = styled(FlexItem)<StyleProps>`
    color: crimson;
`;

export const Hr = styled.div`
    border-top: 2px solid #ccc;
    margin: 1.25rem 0;
`;
